/**
 * CreatureGalleryPage Component
 *
 * Public gallery page displaying all generated creatures in a responsive grid.
 * Features:
 * - Grid layout (4 columns desktop, 2 tablet, 1 mobile)
 * - Pagination with "Load More" button
 * - Click through to creature detail page
 * - Empty state for no creatures
 * - Loading states
 * - In-progress creature generation cards at top of grid
 * - Future-proofed for user filtering
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreatureCard } from '../components/CreatureCard';
import { CreatureCardSkeleton } from '../components/CreatureCardSkeleton';
import { InProgressCreatureCard, type InProgressJob } from '../components/InProgressCreatureCard';
import { PageAtmosphere } from '../components/atmosphere';
import { getCreatureLibraryService } from '../services/creatureLibraryService';
import { fetchInProgressJobs } from '../services/generationService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import '../styles/atmosphere/pages/gallery.css';
import './CreatureGalleryPage.css';

// Future: Add user filter state
// import type { OwnerId } from '@shared/types/creature-storage';
// const [filterOwner, setFilterOwner] = useState<OwnerId | 'all'>('all');

// Poll interval for in-progress jobs (5 seconds)
const IN_PROGRESS_POLL_INTERVAL = 5000;

const CREATURES_PER_PAGE = 24;

export const CreatureGalleryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();
  const [creatures, setCreatures] = useState<SerializedCreatureDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // In-progress jobs state
  const [inProgressJobs, setInProgressJobs] = useState<InProgressJob[]>([]);
  const inProgressPollRef = useRef<NodeJS.Timeout | null>(null);

  // Future: Add user filter state
  // const [filterOwner, setFilterOwner] = useState<OwnerId | 'all'>('all');

  // Fetch in-progress jobs
  const loadInProgressJobs = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const response = await fetchInProgressJobs(user.uid);
      setInProgressJobs(response.jobs);

      // If jobs completed, refresh creatures list
      if (response.jobs.length < inProgressJobs.length) {
        loadCreatures();
      }
    } catch (err) {
      console.error('Failed to load in-progress jobs:', err);
      // Don't show error to user - in-progress is supplementary
    }
  }, [user?.uid, inProgressJobs.length]);

  // Poll for in-progress job updates
  useEffect(() => {
    // Wait for auth to be fully initialized before loading in-progress jobs
    // We need both: authLoading=false AND user to exist (so __getAuthToken is set)
    if (authLoading || !user?.uid) return;

    // Small delay to ensure __getAuthToken is set by AuthContext's useEffect
    const startTimer = setTimeout(() => {
      // Initial fetch
      loadInProgressJobs();

      // Set up polling
      inProgressPollRef.current = setInterval(() => {
        loadInProgressJobs();
      }, IN_PROGRESS_POLL_INTERVAL);
    }, 50);

    return () => {
      clearTimeout(startTimer);
      if (inProgressPollRef.current) {
        clearInterval(inProgressPollRef.current);
      }
    };
  }, [authLoading, user?.uid, loadInProgressJobs]);

  // Load creatures when auth is fully ready (loading done AND user available)
  useEffect(() => {
    // Wait for auth to be fully initialized before loading creatures
    // We need both: authLoading=false AND user to exist (so __getAuthToken is set)
    if (!authLoading && user) {
      // Small delay to ensure __getAuthToken is set by AuthContext's useEffect
      const timer = setTimeout(() => {
        loadCreatures();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [authLoading, user]);

  const loadCreatures = async () => {
    try {
      setLoading(true);
      setError(null);

      const libraryService = getCreatureLibraryService();

      // Load authenticated user's creatures + shared pool
      const response = await libraryService.listCreatures({
        limit: CREATURES_PER_PAGE,
        offset: 0
      });

      setCreatures(response.creatures);
      setTotal(response.total);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Failed to load creatures:', err);
      setError(err instanceof Error ? err.message : 'Failed to load creatures');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      const libraryService = getCreatureLibraryService();

      const response = await libraryService.listCreatures({
        limit: CREATURES_PER_PAGE,
        offset: creatures.length
      });

      setCreatures(prev => [...prev, ...response.creatures]);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Failed to load more creatures:', err);
      setError(err instanceof Error ? err.message : 'Failed to load more creatures');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreatureClick = (creature: SerializedCreatureDocument) => {
    navigate(`/creatures/${creature.id}`);
  };

  // Handle share completion with toast notification
  const handleShareComplete = (success: boolean, message: string) => {
    if (success) {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  // Handle delete completion - remove from list and show toast
  const handleDeleteComplete = (success: boolean, message: string, creatureId: string) => {
    if (success) {
      // Remove creature from local state immediately (optimistic update)
      setCreatures(prev => prev.filter(c => c.id !== creatureId));
      setTotal(prev => Math.max(0, prev - 1));
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const handleCreateNew = () => {
    navigate('/create');
  };

  // Loading state - show 8 skeleton cards in grid
  if (loading) {
    return (
      <PageAtmosphere page="gallery">
        <div className="creature-gallery-page">
          <div className="creature-gallery-page__header">
            <h1>Creature Gallery</h1>
          </div>
          <div className="creature-gallery-page__grid">
            {Array.from({ length: 8 }).map((_, index) => (
              <CreatureCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  // Empty state (only if no completed creatures AND no in-progress jobs)
  if (creatures.length === 0 && inProgressJobs.length === 0 && !error) {
    return (
      <PageAtmosphere page="gallery">
        <div className="creature-gallery-page">
          <div className="creature-gallery-page__header">
            <h1>Creature Gallery</h1>
          </div>
          <div className="creature-gallery-page__empty">
            <div className="empty-state">
              <h2>No Creatures Yet</h2>
              <p>Begin your collection — summon your first creature.</p>
              <button className="btn btn-primary" onClick={handleCreateNew}>
                Create Your First Creature
              </button>
            </div>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  // Main gallery view
  return (
    <PageAtmosphere page="gallery">
      <div className="creature-gallery-page">
        {/* Header */}
        <div className="creature-gallery-page__header">
          <div>
            <h1>Creature Gallery</h1>
            <p className="subtitle">
              {total} {total === 1 ? 'creature' : 'creatures'} in your collection
              {inProgressJobs.length > 0 && (
                <span className="in-progress-count">
                  {' '}&bull; {inProgressJobs.length} being summoned
                </span>
              )}
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleCreateNew}>
            Summon New Creature
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="creature-gallery-page__error">
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={loadCreatures}>
              Retry
            </button>
          </div>
        )}

        {/* Creature Grid */}
        <div className="creature-gallery-page__grid">
          {/* In-Progress Jobs at the top */}
          {inProgressJobs.map(job => (
            <InProgressCreatureCard
              key={job.jobId}
              job={job}
              onClick={() => {
                // Show message that creature is still generating
                console.log(`Creature ${job.jobId} is still generating (${job.progress}%)`);
              }}
            />
          ))}

          {/* Completed Creatures */}
          {creatures.map(creature => (
            <CreatureCard
              key={creature.id}
              creature={creature}
              onClick={handleCreatureClick}
              currentUserId={user?.uid}
              onShareComplete={handleShareComplete}
              onDeleteComplete={handleDeleteComplete}
            />
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="creature-gallery-page__load-more">
            <button
              className="btn btn-secondary"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <span className="spinner-small"></span>
                  Loading...
                </>
              ) : (
                `Load More (${creatures.length} of ${total})`
              )}
            </button>
          </div>
        )}

        {/* Footer */}
        {!hasMore && creatures.length > 0 && (
          <div className="creature-gallery-page__footer">
            <p>Your collection grows with each summoning.</p>
          </div>
        )}
      </div>
    </PageAtmosphere>
  );
};
